'use client';

import React, { FC } from 'react';
import { useInView } from 'react-intersection-observer';
import HelpSection from '@/src/components/sections/helpSection/HelpSection';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import AdvantageList from '@/src/components/sections/advantageList/AdvantageList';
import Label from '@/src/components/ui/label/Label';
import DemoCard from '@/src/components/ui/demoCard/DemoCard';
import { advInfoFull } from '@/src/lib/staticObjects/advInfo';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';

import MoreSection from '@/src/components/screens/promptTheme/moreSection/MoreSection';
import DesignSection from '@/src/components/screens/promptTheme/designSection/DesignSection';
import CtaBanner from '@/src/components/screens/promptTheme/ctaBanner/CtaBanner';
import DemoBanner from '@/src/components/screens/promptTheme/demoBanner/DemoBanner';
import HeroSection from '@/src/components/screens/promptTheme/heroSection/HeroSection';

import styles from './Theme.module.scss';
import { ITheme } from '@/src/lib/types/themeInterface';
import FeaturesSection from '@/src/components/screens/promptTheme/featuresSection/FeaturesSection';
import SubscribeSection from '@/src/components/sections/subscribeSection/SubscribeSection';

type TThemePrompt = {
  theme: ITheme;
};

const ThemePrompt: FC<TThemePrompt> = ({ theme }) => {
  const { ref: inViewRef1, inView: inView1 } = useInView({
    triggerOnce: true,
    rootMargin: `-250px 0px 0px`,
    threshold: 0,
  });

  const { ref: inViewRef2, inView: inView2 } = useInView({
    triggerOnce: true,
    rootMargin: `-250px 0px 0px`,
    threshold: 0,
  });

  const heading = 'Uncompromising Quality';
  const excerpt =
    'Every detail is designed for performance and durability. Discover the features that make our theme stand out from the competition.';

  return (
    <>
      <section ref={inViewRef1} className={styles.demos}>
        <Wrapper className={styles.demosPrompt}>
          <Label>Starter Sites</Label>

          <h2 className={`heading__2 ${styles.heading} ${inView1 && styles.animate__heading}`}>
            {theme.demoSectionTitle &&
              createAnimationArr(theme.demoSectionTitle, styles.word__wrapper, 0, 0.01)}
          </h2>

          <p className={`excerpt ${styles.excerpt} ${inView1 && styles.animate__excerpt}`}>
            {theme.demoSectionDescription &&
              createAnimationArr(theme.demoSectionDescription, styles.word__wrapper, 0.1, 0.01)}
          </p>

          <ul className={styles.themes__list}>
            {theme.demoList?.map((demo, i) => (
              <li key={`${demo._key}-${demo.demoName}`}>
                <DemoCard demoData={demo} index={i} theme={theme} isThemePage />
              </li>
            ))}
            <DemoBanner theme={theme} />
          </ul>
        </Wrapper>
      </section>

      <DesignSection theme={theme} />

      <SubscribeSection themeSlug={theme.slug} colorScheme={'dark'} />

      <HeroSection />
      <CtaBanner theme={theme} />
      <MoreSection />

      <FeaturesSection />

      <section ref={inViewRef2} className={styles.advantage__section}>
        <Wrapper>
          <Label>Additional features</Label>
          <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
            {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
          </h2>
          <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
            {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
          </p>
          <AdvantageList advList={advInfoFull} />
        </Wrapper>
      </section>

      <HelpSection themeSlug={theme.slug} documentationLink={theme.documentationLink} />

      <SubscribeSection
        className={styles.subscribeSection}
        themeSlug={theme.slug}
        colorScheme={'grey'}
      />
    </>
  );
};

export default ThemePrompt;
